import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Drawer,
  Row,
  Col,
  Button,
  Spin,
} from 'antd';
import { DuplicateCardsHelpers } from 'ontraccr-common';
import BoardCardDynamicLastUpdatedIcon from '../boards/BoardCardDynamicLastUpdatedIcon';
import {
  closeCardDrawer,
  openCardDrawer,
} from '../boards/state/boards.actions';
import BoardCardDrawer from '../boards/BoardCardDrawer';
import BoardsSearchDrawer from '../search/BoardsSearchDrawer';
import { searchBoardDuplicates } from './state/forms.actions';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import CustomConfirmModal from '../common/modals/CustomConfirmModal';
import { sortByLocalCompare } from '../helpers/helpers';

const { formatDuplicateCardFields } = DuplicateCardsHelpers;

export default function DuplicateCardsSlider({
  visible,
  onClose,
  allowConfigureConflictDetection = false,
  getConfigureSearchInitialQuery,
  showInDetection = {},
  onResetToDefaultSearch,
  onConfigureSearchActiveChange,
  relativeDateAnchors,
}) {
  const dispatch = useDispatch();

  const boardStatuses = useSelector((state) => state.boards.boardStatuses);
  const boards = useSelector((state) => state.boards.boards);
  const cardTemplates = useSelector((state) => state.boards.cardTemplates);
  const duplicateBoardSearchResults = useSelector((state) => state.forms.duplicateBoardSearchResults); // eslint-disable-line max-len

  const [selectedBoardId, setSelectedBoardId] = useState(null);
  const [configureSearchVisible, setConfigureSearchVisible] = useState(false);
  const [configureInitialQuery, setConfigureInitialQuery] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);

  const ourBoard = useMemo(() => boards[selectedBoardId], [boards, selectedBoardId]);
  const cardType = useMemo(() => cardTemplates[ourBoard?.cardTypeId], [ourBoard, cardTemplates]);

  const statusMap = useMemo(() => {
    const map = {};
    boardStatuses.forEach((status) => {
      map[status.id] = status.title;
    });
    return map;
  }, [boardStatuses]);

  const sortedDuplicateBoardSearchResults = useMemo(() => {
    return duplicateBoardSearchResults.sort(sortByLocalCompare('title', false));
  }, [duplicateBoardSearchResults]);

  // Calculate dynamicLastUpdatedTimestamp for a card
  const getDynamicLastUpdatedTimestamp = useCallback((card) => {
    const { boardId, data = [] } = card;
    const board = boards[boardId];

    if (!board?.enableDynamicLastUpdated || !board?.dynamicLastUpdatedFieldId) {
      return null;
    }

    const [sectionId, fieldId] = board.dynamicLastUpdatedFieldId.split('.');
    const section = data.find((s) => s?.id === sectionId);
    const field = section?.fields?.find((f) => f?.fieldId === fieldId);

    if (!field?.response?.values) return null;

    const timestamps = field.response.values
      .map((v) => v?.dateTimestamp)
      .filter((ts) => ts != null);

    if (timestamps.length > 0) {
      return Math.max(...timestamps);
    }

    return null;
  }, [boards]);

  const closeConfigureSearch = useCallback(() => {
    setConfigureSearchVisible(false);
    setConfigureInitialQuery(null);
    onConfigureSearchActiveChange?.(false);
  }, [onConfigureSearchActiveChange]);

  useEffect(() => {
    if (!visible) {
      closeConfigureSearch();
    }
  }, [visible, closeConfigureSearch]);

  const onDrawerClose = useCallback(() => {
    closeConfigureSearch();
    setSelectedBoardId(null);
    dispatch(closeCardDrawer());
    onClose();
  }, [onClose, closeConfigureSearch]);

  const handleCardColumnClick = useCallback(({ selectedCardId, boardId } = {}) => {
    if (selectedCardId && boardId) {
      setSelectedBoardId(boardId);
      dispatch(openCardDrawer({
        selectedCardId,
      }));
    }
  }, []);

  const onConfigureSearchClick = useCallback(() => {
    const defaultQuery = getConfigureSearchInitialQuery?.() ?? {};
    setConfigureInitialQuery(defaultQuery);
    setConfigureSearchVisible(true);
    onConfigureSearchActiveChange?.(true);
  }, [getConfigureSearchInitialQuery, onConfigureSearchActiveChange]);

  const onConfigureSearchClose = useCallback(() => {
    CustomConfirmModal({
      title: 'Reset conflict search?',
      content: 'Closing will reset the search to the default conflict detection setup.',
      okText: 'Reset',
      cancelText: 'Keep Open',
      onOk: async () => {
        closeConfigureSearch();
        if (onResetToDefaultSearch) {
          setSearchLoading(true);
          await onResetToDefaultSearch();
          setSearchLoading(false);
        }
      },
    });
  }, [closeConfigureSearch, onResetToDefaultSearch]);

  const onConfigureSearch = useCallback(async (query) => {
    setSearchLoading(true);
    const fixedQuery = configureInitialQuery ?? {};
    await dispatch(searchBoardDuplicates(
      {
        ...query,
        boardIds: fixedQuery.boardIds?.length ? fixedQuery.boardIds : query.boardIds,
        cardTypeId: fixedQuery.cardTypeId || query.cardTypeId,
        checkAllBoardsWithSameCardType: !!fixedQuery.checkAllBoardsWithSameCardType,
      },
      showInDetection,
    ));
    setSearchLoading(false);
  }, [configureInitialQuery, showInDetection]);

  if (!visible) return null;

  return (
    <>
      <Drawer
        title={(
          <Row justify="space-between" align="middle" style={{ paddingRight: 24 }}>
            <span>Duplicate Cards</span>
            {allowConfigureConflictDetection && (
              <OnTraccrButton
                title="Configure Search"
                onClick={onConfigureSearchClick}
                style={{ width: 'auto', marginRight: 8 }}
              />
            )}
          </Row>
        )}
        placement="right"
        visible={visible}
        width="42vw"
        mask={false}
        onClose={onDrawerClose}
        bodyStyle={{
          padding: '16px',
          overflowY: 'auto',
          paddingBottom: 80,
        }}
      >
        {searchLoading ? (
          <Row justify="center" align="middle" style={{ minHeight: 200 }}>
            <Spin />
          </Row>
        ) : (
          <div>
            {sortedDuplicateBoardSearchResults.map((card) => {
              const {
                id,
                title,
                data = [],
                boardId,
                statusId,
              } = card;
              // Data is already filtered by showInDetection on the backend
              const fields = data.flatMap((section) => section.fields || []);
              const formattedFields = formatDuplicateCardFields(fields);

              const dynamicLastUpdatedTimestamp = getDynamicLastUpdatedTimestamp(card);

              const boardTitle = boards[boardId]?.title;

              return (
                <div
                  key={id}
                  style={{
                    marginBottom: 24,
                    padding: 16,
                    border: '1px solid #d9d9d9',
                    borderRadius: 4,
                  }}
                >
                  <Row align="middle" style={{ marginBottom: 12 }}>
                    <Col flex="auto">
                      <div style={{ fontWeight: 'bold', fontSize: 16 }}>
                        {title}
                      </div>
                      {boardTitle && (
                        <div style={{
                          fontSize: 14, fontWeight: 500, color: '#434343', marginTop: 6,
                        }}
                        >
                          <span style={{ fontWeight: 400, color: '#8c8c8c' }}>Board: </span>
                          {boardTitle}
                        </div>
                      )}
                      {statusId && (
                        <div style={{
                          fontSize: 14, fontWeight: 500, color: '#434343', marginTop: 4,
                        }}
                        >
                          <span style={{ fontWeight: 400, color: '#8c8c8c' }}>Status: </span>
                          {statusMap[statusId]}
                        </div>
                      )}
                      {dynamicLastUpdatedTimestamp && (
                        <div style={{ marginTop: 4 }}>
                          <BoardCardDynamicLastUpdatedIcon timestamp={dynamicLastUpdatedTimestamp} /> {/* eslint-disable-line max-len */}
                        </div>
                      )}
                    </Col>
                    <Col>
                      <Button
                        type="button"
                        className="link-button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardColumnClick({ selectedCardId: id, boardId });
                        }}
                      >
                        View Card
                      </Button>
                    </Col>
                  </Row>
                  <div>
                    {formattedFields.map((field) => (
                      <Row key={field.title} style={{ marginBottom: 8 }}>
                        <Col span={24}>
                          <div style={{ fontSize: 14 }}>
                            <span style={{ fontWeight: 500, textDecoration: 'underline' }}>
                              {field.title}:
                            </span>
                            <span style={{ paddingLeft: 5 }}>
                              {field.response.value}
                            </span>
                          </div>
                        </Col>
                      </Row>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Drawer>
      {allowConfigureConflictDetection && (
        <BoardsSearchDrawer
          visible={configureSearchVisible}
          onClose={onConfigureSearchClose}
          conditionsOnly
          placement="left"
          mask={false}
          width="55vw"
          title="Configure Conflict Search"
          initialQuery={configureInitialQuery}
          onSearch={onConfigureSearch}
          submitTitle="Search"
          relativeDateAnchors={relativeDateAnchors}
        />
      )}
      {selectedBoardId && ourBoard?.cardTypeId && (
        <BoardCardDrawer
          boardId={selectedBoardId}
          cardTypeId={ourBoard.cardTypeId}
          divisions={ourBoard?.divisions}
          showCardNumber={ourBoard?.showCardNumber}
          useCardNumberAsCardTitle={cardType?.useCardNumberAsCardTitle}
          hideAnalyticsTab
          hideFormsTab
          onClose={() => setSelectedBoardId(null)}
          getContainer={() => document.body}
        />
      )}
    </>
  );
}

DuplicateCardsSlider.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  allowConfigureConflictDetection: PropTypes.bool,
  getConfigureSearchInitialQuery: PropTypes.func,
  showInDetection: PropTypes.shape({}),
  onResetToDefaultSearch: PropTypes.func,
  onConfigureSearchActiveChange: PropTypes.func,
  relativeDateAnchors: PropTypes.objectOf(PropTypes.shape({})),
};

DuplicateCardsSlider.defaultProps = {
  allowConfigureConflictDetection: false,
  getConfigureSearchInitialQuery: undefined,
  showInDetection: {},
  onResetToDefaultSearch: undefined,
  onConfigureSearchActiveChange: undefined,
  relativeDateAnchors: undefined,
};
